import { listTrainingEvalRuns, type TrainingEvalRunSummaryView } from "@/api/trainingEval";
import type { SelectedPolicyRef, TrainingEvalRunScope } from "@/modules/training/public";

// 정책 비교의 그룹 하나다. 한 policy 로 실행한 run 들의 묶음이다.
export type EvalCompareGroup = {
  // 안정적인 고유 id(policy id)다. 시리즈 키가 이 값을 쓴다. 라벨(정책 이름)은 자유 문자열이라
  // 겹칠 수 있다.
  key: string;
  // 표시 이름이다. 정책 이름을 쓴다.
  label: string;
  // 조회 시점의 요약 스냅샷이고, 최신순이다. 그래서 그룹 안의 시드 중복 제거가 최신 run 을 남긴다.
  runs: readonly TrainingEvalRunSummaryView[];
  // 서버가 아는 succeeded run 의 전체 수다. runs.length 보다 크면 최신 한 페이지만 담은 것이다.
  // 뷰어는 잘렸다는 사실을 표시한다.
  totalRuns: number;
};

// 그룹들을 controller 가 받는 평평한 run 목록으로 바꾼다. run 의 색 슬롯은 소속 그룹 순번과 같다.
// 그래서 run 색을 어디에 쓰든 그룹 시리즈 색(같은 순번의 팔레트 슬롯)과 같다.
// 이 불변 조건은 이 함수만 소유한다.
export function flattenCompareGroups(groups: readonly EvalCompareGroup[]): {
  runs: readonly TrainingEvalRunSummaryView[];
  slotById: Readonly<Record<string, number>>;
} {
  const slotById: Record<string, number> = {};
  groups.forEach((group, index) => {
    for (const run of group.runs) {
      slotById[run.id] = index;
    }
  });
  return { runs: groups.flatMap((group) => group.runs), slotById };
}

// 정책당 풀링에 담는 최대 run 수다. 이 값은 MAX_SEEDS(한 번의 평가 제출이 만드는 최대 시드 수)와
// 같다. 그래서 최신 제출 1회분의 시드를 모두 포함한다. 이보다 run 이 많은 정책은 최신 32개만
// 평균에 들어간다. 비교 창은 total 로 잘렸다는 사실을 표시한다. 상한을 늘리면 비교 창의 run 별
// 상세 로드가 정책 수 × 이 값 × 2 요청으로 커진다. 상한을 바꿀 때 이 요청 수를 함께 계산한다.
export const POLICY_COMPARE_RUN_PAGE_SIZE = 32;

/**
 * 정책 비교의 그룹 데이터를 받는다. 정책마다 최근 succeeded run 한 페이지를 병렬로 받는다.
 *
 * run 요약에는 policy_id 가 없다. 그래서 run 을 정책에 정확히 연결하는 방법은 정책당 요청
 * 1건(단일 policy_id 필터)뿐이다. 정책 수는 선택 상한(15)까지다. 그래서 한 번의 병렬 요청은
 * 1왕복이다(rules.md §7). 최신순으로 조회하므로 그룹 안의 시드 중복 제거가 정책별 최신 run 을 남긴다.
 *
 * 요청이 하나라도 실패하면 전체를 실패로 처리한다. 일부 정책이 경고 없이 빠진 비교는 오해를 만든다.
 * succeeded run 이 없는 정책은 빈 그룹으로 유지한다. 그래서 비교 창은 "0 runs" 를 그대로 보여준다.
 */
export async function fetchPolicyCompareGroups(
  policies: readonly SelectedPolicyRef[],
  scope: TrainingEvalRunScope,
): Promise<EvalCompareGroup[]> {
  const pages = await Promise.all(
    policies.map((policy) =>
      listTrainingEvalRuns(
        scope,
        { page: 1, size: POLICY_COMPARE_RUN_PAGE_SIZE },
        { status: "succeeded" },
        "started_at_desc",
        [policy.policyId],
      ),
    ),
  );
  return policies.map((policy, index) => ({
    key: policy.policyId,
    label: policy.policyName,
    runs: pages[index].items,
    // 서버가 아는 succeeded run 의 전체 수다. 창은 이 값으로 한 페이지를 넘는 정책이 잘렸다고 표시한다.
    totalRuns: pages[index].total,
  }));
}
